import { type FC, type MutableRefObject, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { RiSquareFill } from 'react-icons/ri';
import { type StudyApiId } from '@/model/Study.model';
import { _isNil, _isNotEmpty, _notNil } from '@/littledash';
import {
  type AnimalMap,
  type GroupMap,
  type SampleTypeMap,
  type MetaField,
  type ReservedSample,
  type ReservedSampleApiId,
  type SampleDefinitionMap,
} from './SampleNames.model';
import { renderTimepoint, getMetaValue } from './SampleNames.utils';
import ReservedSampleStatusChip from './ReservedSampleStatusChip';
import { type GroupApiId, type TreatmentGroup } from '@/model/TreatmentGroup.model';
import APITable from '@/components/UI/Table/Reusable/APITable';
import type { TanStackColumnDef, FilterOption } from '@/components/UI/Table/TableComponent.model';
import type { TableFilter } from '@/components/UI/Table/Reusable/APITable/APITable.model';
import { FilterTypes as filterType } from '@/constants/FilterTypes';
import { IndeterminateCheckbox } from '@/components/UI/Table/Columns/Selector';
import { RouteService } from '@/support/RouteService';
import { web as webRoute } from '@/support/route';
import styles from './SampleNamesTable.module.scss';
import type { UserTableViewSettings } from '@/model/UserTableView.model';
import { type AnimalApiId, AnimalSexMap } from '@/model/Animal.model';
import type { ID } from '@/model/Common.model';
import { DateRenderer } from '@/components/UI/DateRenderers/DateRenderers.tsx';

const OPT_IN_COLUMN_IDS = new Set(['sex', 'animal_number', 'cage_name', 'terminated_at']);
const isOptIn = (id: string) => OPT_IN_COLUMN_IDS.has(id) || id.startsWith('meta_');

const GroupChip: FC<{ group: TreatmentGroup | undefined }> = ({ group }) => {
  if (_isNil(group)) {
    return null;
  }
  return (
    <div className="flex flex-row items-center" style={{ gap: '0.25rem' }}>
      {_notNil(group.color) && <RiSquareFill color={group.color} />}
      <span>{group.name}</span>
    </div>
  );
};

/**
 * Flat (ungrouped) view of reserved samples for a study, rendered as a paginated table.
 *
 * Two fetch modes:
 * - **GET mode** (no `reservedSampleApiIds`): loads the full study list via GET.
 * - **POST mode** (`reservedSampleApiIds` provided): fetches only the specified samples via
 *   POST list-by-ids, keeping all APITable features (search, column visibility, pagination).
 */
interface ReservedSamplesFlatTableProps {
  studyApiId: StudyApiId;
  studyId?: ID;
  animalMap?: AnimalMap;
  groupMap: GroupMap;
  sampleTypeMap: SampleTypeMap;
  metaFields: Array<MetaField>;
  sampleDefinitionMap: SampleDefinitionMap;
  selectedReservedSampleIds: Set<ReservedSampleApiId>;
  toggleReservedSampleIds: (ids: Array<ReservedSampleApiId>) => void;
  /** When provided, fetches only these samples via POST list-by-ids instead of GET all. */
  reservedSampleApiIds?: Array<ReservedSampleApiId>;
  filterCollected?: boolean;
  disablePlanned?: boolean;
  disableUnassigned?: boolean;
  hideBorder?: boolean;
  refetch?: number | string;
  onSamplesLoaded?: (samples: Array<ReservedSample>) => void;
  columnVisibility?: Record<string, boolean>;
  getSettingsRef?: MutableRefObject<(() => UserTableViewSettings) | null>;
  activeSettingsOverride?: UserTableViewSettings;
  defaultFilters?: Array<TableFilter>;
  selectionCountBadge?: number;
  reduxTableName?: string;
}

const ReservedSamplesFlatTable: FC<ReservedSamplesFlatTableProps> = ({
  studyApiId,
  studyId,
  animalMap,
  groupMap,
  sampleTypeMap,
  metaFields,
  sampleDefinitionMap,
  selectedReservedSampleIds,
  toggleReservedSampleIds,
  reservedSampleApiIds,
  filterCollected = false,
  disablePlanned = false,
  disableUnassigned = true,
  hideBorder = true,
  refetch,
  onSamplesLoaded,
  columnVisibility,
  getSettingsRef,
  activeSettingsOverride,
  defaultFilters,
  selectionCountBadge,
  reduxTableName = 'reservedSamples',
}) => {
  const columns = useMemo<Array<TanStackColumnDef<ReservedSample>>>(() => {
    const staticColumns: Array<TanStackColumnDef<ReservedSample>> = [
      {
        id: 'selection',
        minSize: 40,
        maxSize: 40,
        enableResizing: false,
        enableSorting: false,
        meta: { className: 'ui__selector-column' },
        header: ({ table }) => {
          const selectableIds = table
            .getRowModel()
            .rows.filter(
              (r) =>
                r.original.status !== 'collected' &&
                r.original.status !== 'cancelled' &&
                !(disablePlanned && r.original.status === 'planned') &&
                _notNil(r.original.animal_api_id)
            )
            .map((r) => r.original.api_id);
          const allSelected =
            selectableIds.length > 0 && selectableIds.every((id) => selectedReservedSampleIds.has(id));
          const someSelected = !allSelected && selectableIds.some((id) => selectedReservedSampleIds.has(id));
          return (
            <div data-test-element="select-all-header" className="flex items-center h-100">
              <IndeterminateCheckbox
                checked={allSelected}
                indeterminate={someSelected}
                disabled={selectableIds.length === 0}
                onChange={() => toggleReservedSampleIds(selectableIds)}
                title="Toggle All Rows Selected"
              />
            </div>
          );
        },
        cell: ({ row }) => {
          const disabledReason =
            row.original.status === 'cancelled'
              ? 'Sample cancelled'
              : disablePlanned && row.original.status === 'planned'
                ? 'Planned sample already created'
                : disableUnassigned && _isNil(row.original.animal_api_id)
                  ? 'Must be assigned to a registered animal'
                  : undefined;
          return (
            <span
              data-tooltip-id="global-tooltip-id"
              data-tooltip-content={disabledReason}
              data-tooltip-hidden={_isNil(disabledReason)}
            >
              <IndeterminateCheckbox
                checked={selectedReservedSampleIds.has(row.original.api_id)}
                disabled={_notNil(disabledReason)}
                aria-label="Select sample"
                onChange={() => toggleReservedSampleIds([row.original.api_id])}
              />
            </span>
          );
        },
      },
      {
        id: 'name',
        header: 'Sample Name',
        accessorKey: 'name',
        enableSorting: true,
        enableResizing: true,
        size: 200,
        cell: ({ row }) => <span>{row.original.name}</span>,
      },
      {
        id: 'sample_definition',
        header: 'Sample Definition',
        accessorFn: (row) => row.sample_definition_api_id,
        cell: ({ row }) => {
          const defApiId = row.original.sample_definition_api_id;
          if (_isNil(defApiId)) {
            return '';
          }
          return sampleDefinitionMap.get(defApiId)?.name ?? defApiId;
        },
        enableSorting: true,
        enableResizing: true,
        size: 200,
      },
      {
        id: 'study_group',
        header: 'Study Group',
        accessorFn: (row) => row.study_group_api_id,
        cell: ({ row }) => {
          const group = _notNil(row.original.study_group_api_id)
            ? groupMap.get(row.original.study_group_api_id as GroupApiId)
            : undefined;
          return <GroupChip group={group} />;
        },
        enableSorting: true,
        enableResizing: true,
        size: 160,
      },
      {
        id: 'planned_animal',
        header: 'Planned Animal',
        accessorFn: (row) => row.planned_animal_id ?? '',
        enableSorting: true,
        enableResizing: true,
        size: 140,
      },
      {
        id: 'animal',
        header: 'Animal',
        accessorFn: (row) => row.animal_name ?? '',
        cell: ({ row }) => {
          const animalApiId = row.original.animal_api_id as AnimalApiId | undefined;
          const animalEntry = _notNil(animalApiId) ? animalMap?.get(animalApiId) : undefined;
          const animalNumericId =
            _notNil(animalEntry) && animalEntry.type === 'registered' && _notNil(animalEntry.id)
              ? animalEntry.id
              : null;
          return _notNil(animalNumericId) && _notNil(studyId) ? (
            <Link
              to={webRoute('animals.show', { id: studyId, subject_id: animalNumericId })}
              className="link blue"
              target="_blank"
              rel="noreferrer"
            >
              {row.original.animal_name}
            </Link>
          ) : (
            (row.original.animal_name ?? '')
          );
        },
        enableSorting: true,
        enableResizing: true,
        size: 140,
      },
      {
        id: 'status',
        header: 'Status',
        accessorFn: (row) => row.status,
        cell: ({ row }) => <ReservedSampleStatusChip status={row.original.status} />,
        enableSorting: true,
        enableResizing: true,
        size: 120,
      },
      {
        id: 'timepoint',
        header: 'Timepoint',
        accessorFn: (row) => row.timepoint ?? '',
        cell: ({ row }) => (_notNil(row.original.timepoint) ? renderTimepoint(row.original.timepoint) : ''),
        enableSorting: true,
        enableResizing: true,
        size: 160,
      },
      {
        id: 'sample_type',
        header: 'Sample Type',
        accessorFn: (row) => {
          const defApiId = row.sample_definition_api_id;
          if (_isNil(defApiId)) {
            return '';
          }
          const sampleDef = sampleDefinitionMap.get(defApiId);
          return _notNil(sampleDef) ? (sampleTypeMap.get(sampleDef.sample_type) ?? sampleDef.sample_type) : '';
        },
        enableSorting: false,
        enableResizing: true,
        size: 140,
      },
      {
        id: 'animal_number',
        header: 'Animal No.',
        accessorFn: (row) => row.animal_number ?? '',
        enableSorting: true,
        enableResizing: true,
        size: 100,
        isVisible: false,
      },
      {
        id: 'sex',
        header: 'Sex',
        accessorFn: (row) => (row.sex != null ? (AnimalSexMap[row.sex] ?? row.sex) : ''),
        enableSorting: true,
        enableResizing: true,
        size: 80,
        isVisible: false,
      },
      {
        id: 'cage_name',
        header: 'Cage Name',
        accessorFn: (row) => row.cage_name ?? '',
        enableSorting: false,
        enableResizing: true,
        size: 140,
        isVisible: false,
      },
      {
        id: 'terminated_at',
        header: 'Deceased Date',
        accessorFn: (row) => row.terminated_at ?? '',
        cell: ({ row }) => <DateRenderer value={row.original.terminated_at} defaultResponse="" />,
        enableSorting: false,
        enableResizing: true,
        size: 140,
        isVisible: false,
      },
    ];

    const metaColumns = metaFields.map<TanStackColumnDef<ReservedSample>>((field) => ({
      id: `meta_${field.apiId}`,
      header: field.title,
      accessorFn: (row) => {
        const defApiId = row.sample_definition_api_id;
        if (_isNil(defApiId)) {
          return '';
        }
        const sampleDef = sampleDefinitionMap.get(defApiId);
        return _notNil(sampleDef) ? getMetaValue(sampleDef.metadata ?? [], field.apiId) : '';
      },
      enableSorting: false,
      enableResizing: true,
      size: 160,
      isVisible: false,
    }));

    if (_notNil(columnVisibility)) {
      return [...staticColumns, ...metaColumns]
        .filter((col) => (isOptIn(col.id) ? columnVisibility[col.id] === true : columnVisibility[col.id] !== false))
        .map((col) => (isOptIn(col.id) ? { ...col, isVisible: undefined } : col));
    }
    return [...staticColumns, ...metaColumns];
  }, [
    sampleDefinitionMap,
    groupMap,
    sampleTypeMap,
    metaFields,
    selectedReservedSampleIds,
    toggleReservedSampleIds,
    columnVisibility,
    animalMap,
    studyId,
    disablePlanned,
  ]);

  const groupOptions = useMemo(
    () => Array.from(groupMap.entries()).map(([apiId, group]) => ({ value: String(apiId), name: group.name })),
    [groupMap]
  );

  const filterOptions = useMemo<Array<FilterOption>>(() => {
    const options: Array<FilterOption> = [
      {
        value: 'deceased',
        name: 'Living status',
        operations: [
          { name: 'is equal to', value: filterType.eq, options: [{ name: 'Alive', value: 'null' }] },
          { name: 'is not equal to', value: filterType.ne, options: [{ name: 'Alive', value: 'null' }] },
        ],
      },
      {
        value: 'sex',
        name: 'Sex',
        operations: [
          {
            name: 'is equal to',
            value: filterType.eq,
            options: [
              { value: 'm', name: 'Male' },
              { value: 'f', name: 'Female' },
              { value: 'u', name: 'Unknown' },
            ],
          },
        ],
      },
      {
        value: 'cage',
        name: 'Cage name',
        operations: [
          { name: 'is equal to', value: filterType.eq, options: [] },
          { name: 'is not equal to', value: filterType.ne, options: [] },
          { name: 'contains', value: filterType.contains },
          { name: 'does not contain', value: filterType.not_contains },
        ],
        ...(_notNil(studyId) && {
          fetchFromApi: {
            placeholder: 'Search cages',
            url: 'studies.cages.list',
            params: { studyId },
            queryParams: { sort: 'catalog', order: 'asc', perPage: -1 },
            tableFilterMenuProperty: 'api_id',
          },
        }),
      },
      {
        value: 'study_group',
        name: 'Study Group',
        operations: [
          { name: 'is equal to', value: filterType.eq, options: groupOptions },
          { name: 'is not equal to', value: filterType.ne, options: groupOptions },
          { name: filterType.IS_NULL, value: filterType.IS_NULL },
          { name: filterType.IS_NOT_NULL, value: filterType.IS_NOT_NULL },
        ],
        ...(_notNil(studyId) && {
          fetchFromApi: {
            placeholder: 'Search groups',
            url: 'studies.groups.list',
            params: { id: studyId },
            queryParams: { perPage: -1 },
            tableFilterMenuProperty: 'api_id',
          },
        }),
      },
      {
        value: 'timepoint',
        name: 'Timepoint',
        operations: [
          { name: 'equals', value: filterType.eq_interval },
          { name: 'contains', value: filterType.contains },
        ],
      },
    ];

    if (_isNotEmpty(sampleDefinitionMap)) {
      options.push({
        value: 'sample_definition',
        name: 'Sample Definition',
        operations: [
          {
            name: 'is equal to',
            value: filterType.eq,
            options: Array.from(sampleDefinitionMap.entries()).map(([apiId, def]) => ({
              value: apiId,
              name: def.name,
            })),
          },
          {
            name: 'is not equal to',
            value: filterType.ne,
            options: Array.from(sampleDefinitionMap.entries()).map(([apiId, def]) => ({
              value: apiId,
              name: def.name,
            })),
          },
        ],
      });
    }

    return options;
  }, [studyId, sampleDefinitionMap, groupOptions]);

  const handleFilterData = useCallback(
    (data: Array<ReservedSample>) => (filterCollected ? data.filter((s) => s.status !== 'collected') : data),
    [filterCollected]
  );

  const idsProvided = _notNil(reservedSampleApiIds);
  const hasIds = idsProvided && reservedSampleApiIds.length > 0;

  const apiInfo = useMemo(() => {
    if (hasIds) {
      return {
        type: 'internalApiPost' as const,
        route: RouteService.api({
          endpoint: 'POST /api/v1/studies/{studyApiId}/planned/samples/reserved-samples/list',
          path: { studyApiId },
        }).url,
        body: { reserved_sample_api_ids: reservedSampleApiIds },
      };
    }
    const url = RouteService.api({
      endpoint: 'GET /api/v1/studies/{studyApiId}/planned/samples/reserved-samples',
      path: { studyApiId },
    }).url;
    url.searchParams.append('include[]', 'CAGE_NAME');
    return { type: 'internalApi' as const, route: url };
  }, [studyApiId, hasIds, reservedSampleApiIds]);

  if (idsProvided && !hasIds) {
    return (
      <div
        className={hideBorder ? styles.FlatTableContainer : undefined}
        data-test-component="ReservedSamplesFlatTable"
        data-test-element="container"
      >
        <div className="flex flex-column items-center bg-light-gray ma4 pa4">
          <div className="f4 lh-copy">No Sample Names</div>
          <div className="mv4">No samples selected</div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={hideBorder ? styles.FlatTableContainer : undefined}
      data-test-component="ReservedSamplesFlatTable"
      data-test-element="container"
    >
      <APITable<ReservedSample>
        columns={columns}
        apiInfo={apiInfo}
        filterData={filterCollected ? handleFilterData : undefined}
        refetch={refetch}
        onFetchTableData={onSamplesLoaded}
        reduxTableName={reduxTableName}
        hideShow={_notNil(columnVisibility)}
        selectionCountBadge={selectionCountBadge}
        searchPlaceholderText="Search samples"
        searchQueryByOptions={[
          { value: 'name', display: 'Name' },
          { value: 'animal', display: 'Animal' },
          { value: 'planned_animal', display: 'Planned Animal' },
        ]}
        filterOptions={filterOptions}
        defaultFilters={defaultFilters}
        defaultSortBy={{ id: 'planned_animal', desc: false }}
        defaultPageSize={1000}
        testIdPrefix="reserved-samples-flat"
        getSettingsRef={getSettingsRef}
        activeSettingsOverride={activeSettingsOverride}
      />
    </div>
  );
};

export default ReservedSamplesFlatTable;
